import * as Papa from 'papaparse'

import { toolTuningFields } from '../ccm'
import { kqlEscape } from '../escape-utils'
import { CustomerMapParsed, FieldMapping, SupportedTools } from '../interfaces'

import { RenderOptions, Tool } from './base'
import {
  etaFieldLookup,
  InlineGroups,
  normalizeList,
  RenderContext,
  scalarToString,
  makeRenderer,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import { wrapRendererWithDatasource } from './render/rtd/datasource.utils'
import {
  splunkEventCodeByLogCategory,
  splunkMap,
} from './render/rtd/tool-datasource-mappings/splunk.datasource'
import { AstPredicate } from './render/types'

// Wrap a resolved field name in Eta markup for Matchboi substitution
const eta = (f: string) => `{{!=${f}!}}`

// DataDisplay template for Splunk.
// Fields are wrapped in Eta markup ({{!=field!}}) so Matchboi resolves them at render time.
// LogScale does NOT use Eta — this is Splunk-specific because renderContentTemplate handles field substitution.
export const splunkDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields) => `| table ${fields.map(eta).join(', ')}`,

  groupBy: (groupFields, aggClauses) => {
    const aggs = aggClauses.length ? aggClauses.join(', ') : 'count'
    return `| stats ${aggs} by ${groupFields.map(eta).join(', ')}`
  },

  timeBucket: (span, groupFields, aggClauses) => {
    const agg = aggClauses[0] ?? 'count'
    const by = groupFields.length
      ? ` by ${groupFields.map(eta).join(', ')}`
      : ''
    return `| timechart span=${span} ${agg}${by}`
  },

  aggFns: {
    count: (_field, alias) =>
      alias && alias !== 'count' ? `count AS ${alias}` : 'count',
    dcount: (field, alias) => `dc(${eta(field)}) AS ${alias}`,
    values: (field, alias) => `values(${eta(field)}) AS ${alias}`,
    collect: (field, alias) => `values(${eta(field)}) AS ${alias}`,
    first: (field, alias) => `first(${eta(field)}) AS ${alias}`,
    last: (field, alias) => `last(${eta(field)}) AS ${alias}`,
    sum: (field, alias) => `sum(${eta(field)}) AS ${alias}`,
    avg: (field, alias) => `avg(${eta(field)}) AS ${alias}`,
    min: (field, alias) => `min(${eta(field)}) AS ${alias}`,
    max: (field, alias) => `max(${eta(field)}) AS ${alias}`,
  },

  fieldAlias: (field, alias) => `${eta(field)} AS ${alias}`,

  join: (body, ddClause) => `${body}\n${ddClause}`,
}

export { splunkMap }

export class SplunkTool extends Tool {
  public readonly toolName = 'Splunk'
  public readonly toolKind = 'siem' as const
  public override readonly customMapping = true
  public override readonly toolSpecific = ['index', 'sourceType']
  public override readonly datasourceMap = splunkMap

  public override supportedLogCategories(): string[] {
    return [
      'endpoint:sysmon',
      'endpoint:wineventlog',
      'network',
      'web',
      'http',
      'dns',
      'cloud',
    ]
  }
  public readonly dataDisplayTemplate = splunkDataDisplayTemplate

  // Temporary until a better global default for Splunk can be built
  private defaultFieldMapping: FieldMapping = {
    name: 'foo',
    category: 'foo',
    fields: [],
    toolSpecific: [{ index: '*' }, { sourceType: '*' }],
  }

  /**
   * Extended to prefix content with index + sourcetype from the fieldMapping
   */
  public override renderContentTemplate(
    contentTemplate: string,
    pivotFields?: { [fieldName: string]: string },
    fieldMapping?: FieldMapping,
    options: RenderOptions = {},
  ): string {
    fieldMapping = fieldMapping ?? this.defaultFieldMapping

    const generatedContent = super.renderContentTemplate(
      contentTemplate,
      pivotFields,
      fieldMapping,
      options,
    )

    if (options.includeToolPrefix === false) {
      return generatedContent
    }

    const index = Object.values(fieldMapping.toolSpecific[0])[0] ?? '*'
    const sourcetype = Object.values(fieldMapping.toolSpecific[1])[0] ?? '*'

    return `index=${index} sourcetype=${sourcetype} ${generatedContent}`
  }

  /**
   * Parse CSV export contents into known format
   * @param fileContents Splunk export CSV
   */
  public parseMapUpload(fileContents: string): CustomerMapParsed {
    const csvParsed = Papa.parse<string[]>(fileContents, {
      skipEmptyLines: true,
    }).data

    // remove header line
    csvParsed.shift()

    return csvParsed.map((row) => {
      return {
        index: row[0],
        sourcetype: row[1],
        fields: row[2].split(', '),
      }
    })
  }

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${rootURL}en-US/app/launcher/search?q=${encodedRenderedContent}`
  }

  public pivotEscape(key: string, value: string): string {
    return kqlEscape(value)
  }

  /** Implementation Notes
   * Fields
   * - Map via toolTuningFields["splunk"]; wrap with Eta: `{{!=<mapped>!}}`.
   *
   * Values & Lists
   * - Always **double-quoted** (even numbers), escape \ and ".
   * - Multi-values use `IN ("v1","v2",...)`.
   *
   * Logic & Grouping
   * - Uses InlineGroups: **no root parentheses**; nested groups parenthesized.
   * - Logical glue: ` AND ` / ` OR `.
   *
   * Operators
   * - eq / neq: `F = "v"` / `NOT F = "v"`; lists: `F IN (...)` / `NOT F IN (...)`.
   * - contains / doesnotcontain: `F = "*v*"` / `NOT F = "*v*"`.
   * - startswith / endswith: `F = "v*"` / `F = "*v"`.
   * - Comparisons: `F < "<v>"`, `<=`, `>`, `>=` (quoted).
   *
   * Null / Empty
   * - isnull → `NOT F=*`; isnotnull → `F=*`; isempty → `F IN ("","-")`; isnotempty → `NOT F IN ("","-")`.
   *
   * Safety
   * - Sanitize identifiers; use centralized quoting/list helpers.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName, // Inputs splunk as the tool name in the render-kit config -- this is matched to fieldMaps file
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => etaFieldLookup(f), // safe Eta key-lookup (SSTI-hardened)
      andGlue: ' AND ', // Capitalization matters, must be all caps
      orGlue: ' OR ', // Capitalization matters, must be all caps
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))

      // null/empty
      // static syntax, only pulls in the field name, no value or list is supported
      switch (p.op) {
        case 'isnull':
          return `NOT ${F}=*`
        case 'isnotnull':
          return `${F}=*`
        case 'isempty':
          return `${F} IN ("","-")`
        case 'isnotempty':
          return `NOT ${F} IN ("","-")`
      }

      // Format all values and check if multi-value from ast
      const rawVals =
        p.value == null
          ? [undefined]
          : Array.isArray(p.value)
            ? p.value
            : [p.value]
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = rawVals.length > 1

      // Format every value's wildcards based on conditional operator
      const shaped = vals.map((s) => {
        switch (p.op) {
          case 'contains':
            return `*${s}*`
          case 'doesnotcontain':
            return `*${s}*`
          case 'startswith':
            return `${s}*`
          case 'endswith':
            return `*${s}`
          default:
            return s
        }
      })

      const V = (s: string) => renderContext.quote(s)
      const LIST = () => renderContext.list(shaped)

      switch (p.op) {
        case 'eq':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'neq':
          return isMulti
            ? `NOT (${F} IN ${LIST()})`
            : `NOT (${F} = ${V(shaped[0])})`
        case 'contains':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'doesnotcontain':
          return isMulti
            ? `NOT (${F} IN ${LIST()})`
            : `NOT (${F} = ${V(shaped[0])})`
        case 'startswith':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'endswith':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'lt':
          return `${F} < ${V(shaped[0])}`
        case 'lte':
          return `${F} <= ${V(shaped[0])}`
        case 'gt':
          return `${F} > ${V(shaped[0])}`
        case 'gte':
          return `${F} >= ${V(shaped[0])}`
        default:
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
      }
    },
  })

  public readonly tuningText =
    'Insert tuning logic at the end of the first line, after the base logic. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'

  /** Emit the Splunk datasource prelude from a resolved NormalizedDataSource.
   *
   * Index/sourcetype are NOT emitted here — Matchboi's renderContentTemplate
   * handles those from the customer's FieldMapping.
   *
   * The prelude is scoped by the customer's log source (`ds.logCategory`,
   * attached by `renderSiemGrouped`). We look up Windows Security / Sysmon
   * event ids from `splunkEventCodeByLogCategory` keyed by
   * (logCategory, huntCategory). Example output:
   *   endpoint:sysmon + process_creation       → EventCode=1
   *   endpoint:wineventlog + process_creation  → EventCode=4688
   *   endpoint:sysmon + dns                    → EventCode=22
   *
   * Log source / hunt category pairs without a mapped event id (e.g. web,
   * http, cloud; wineventlog + dns) produce no prelude — the query body
   * alone is returned and the joiner skipped upstream.
   */
  public emitSplunkDatasource(
    ds: NormalizedDataSource | undefined,
    tool: SupportedTools,
  ): string {
    if (!ds?.logCategory || !ds.categories?.length) return ''

    const logCatTable = splunkEventCodeByLogCategory[ds.logCategory]
    if (!logCatTable) return ''

    const codes: string[] = []
    for (const cat of ds.categories) {
      const entry = logCatTable[cat]
      if (entry?.length) codes.push(...entry)
    }
    const eventCodes = Array.from(new Set(codes))
    if (!eventCodes.length) return ''

    const eventIdField = `{{!=${toolTuningFields[tool]?.['event_id'] ?? 'EventCode'}!}}`
    return eventCodes.length === 1
      ? `${eventIdField}=${eventCodes[0]}`
      : `${eventIdField} IN (${eventCodes.join(',')})`
  }

  /** Merge the base logic (tuning) with EventCode datasource prelude, dataDisplay, and other directives */
  public readonly renderSplunk = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitSplunkDatasource(ds, this.toolName),
      (prelude, body) => `${prelude} AND (${body})`,
      this.tuningText,
    ),
    this.toolName,
    splunkDataDisplayTemplate,
  )
}
